import React, { PropsWithChildren } from 'react';
import {
  ProductHourAndDate,
  Product,
  ProductName,
  Price,
  PackageTotalPrice,
  PackagePriceInfo,
  AdditionalWrapper,
  ProductProvider,
} from '@src/components/Checkout/styles';

import { formatCents } from '@src/common';

import { getTariffTotalPrice } from '@src/domains/tariffs';
import { formatItemSchedule } from '@src/common/utils/formatItemSchedule';
import { DetailedSaleItem as Item } from '@src/core/buyerApiClient';
import useTranslate from '@src/i18n/useTranslate';
import { appInfoSelector } from '@src/stores/app';
import { useAppSelector } from '@src/core/hooks';
import { ErrorWarning } from '@src/features/cart';
import useShowItemPrices from '@src/hooks/useShowItemPrices';

interface Props {
  item: Item;
  briefView?: boolean;
}

const SaleItem = (props: PropsWithChildren<Props>) => {
  const { item, children, briefView = false } = props;

  const { i18n } = useTranslate();
  const appInfo = useAppSelector(appInfoSelector);

  const showItemPrices = useShowItemPrices();

  return (
    <Product key={item.id}>
      <ProductHourAndDate>
        {formatItemSchedule(item, {
          datePrefix: i18n('checkout_purchase_summary_day'),
          fallbackText: i18n('checkout_purchase_summary_without_date'),
        })}
      </ProductHourAndDate>

      <ProductName>
        {item.product.name} ({item.quantity})
      </ProductName>

      {item.product.appId !== appInfo?.id ? (
        <ProductProvider>{i18n('product_package_adder_sold_by', { owner: item.product.app.name })}</ProductProvider>
      ) : null}

      {showItemPrices ? <Price>{formatCents(getTariffTotalPrice(item.tariffs))}</Price> : null}
      {item.resources && !briefView
        ? item?.resources.map((saleResource) => (
            <AdditionalWrapper key={saleResource.resource.name}>
              <ProductName>{saleResource.resource.formattedName}</ProductName>

              {saleResource?.priceCents && showItemPrices ? (
                <Price>{formatCents(saleResource?.priceCents)}</Price>
              ) : null}
            </AdditionalWrapper>
          ))
        : null}

      {item.additionals && !briefView
        ? item.additionals.map((saleAdditional) => (
            <AdditionalWrapper key={saleAdditional.id}>
              <ProductName>
                {saleAdditional.additional.title} ({saleAdditional.unitQuantity})
              </ProductName>

              {showItemPrices ? <Price>{formatCents(saleAdditional.priceCents)}</Price> : null}
            </AdditionalWrapper>
          ))
        : null}

      {children}

      {item.productPackageOriginalDiscountCurrencyInfo &&
      Number.isFinite(item.productPackageOriginalDiscountCurrencyInfo.amountCents) ? (
        <ErrorWarning variant='warning'>{i18n('discount_fallback_warning_message')}</ErrorWarning>
      ) : null}
    </Product>
  );
};

export const SaleItemWithPrice = ({ item, briefView = false }: Props) => {
  const { i18n } = useTranslate();
  const showItemPrices = useShowItemPrices();

  return (
    <SaleItem item={item} briefView={briefView}>
      {!briefView && showItemPrices ? (
        <PackagePriceInfo>
          {i18n('cart_package_total')}
          <PackageTotalPrice>{formatCents(item.totalPriceCurrencyInfo.amountCents)}</PackageTotalPrice>
        </PackagePriceInfo>
      ) : null}
    </SaleItem>
  );
};

export default SaleItem;
